import React, { useState, useEffect, useRef } from 'react';
import { Button, Icon, Progress, Grid } from 'semantic-ui-react';
import './audioPlayer.css';

const formatTime = (seconds) => {
  // Handle invalid input
  if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
    return '0:00';
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
};

// Simple audio player using native HTML5 Audio
const SimpleAudioPlayer = ({
  src,
  header,
  onReload,
  autoPlay = false,
  fileDuration = null // Prop for server-provided duration
}) => {
  // Refs
  const audioRef = useRef(null);
  const mountedRef = useRef(true);
  const wasPlayingRef = useRef(false); // Track if audio was playing before a rerender
  const playbackPositionRef = useRef(0); // Track the playback position
  const fileDurationRef = useRef(null); // Store the fileDuration for easy access

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [usingLocalFile, setUsingLocalFile] = useState(false);

  // Create a new audio element on mount - don't reuse across instances
  useEffect(() => {
    // Create an actual DOM audio element instead of using the Audio constructor
    const audioElement = document.createElement('audio');
    audioElement.controls = false; // Hide default controls
    audioElement.style.display = 'none'; // Hide the element
    audioElement.crossOrigin = 'anonymous'; // Try to avoid CORS issues
    audioElement.preload = 'auto'; // Preload the audio

    // Add the audio element to the DOM
    document.body.appendChild(audioElement);

    // Store a reference to the audio element
    audioRef.current = audioElement;

    // Clean up on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';

        // Remove the audio element from the DOM
        try {
          document.body.removeChild(audioRef.current);
        } catch (e) {
          console.error('SimpleAudioPlayer: Error removing audio element from DOM:', e);
        }

        audioRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Handle fileDuration changes
  useEffect(() => {
    // If we have a valid fileDuration from the server, use it immediately
    if (fileDuration !== null && isFinite(fileDuration) && fileDuration > 0) {
      // Force duration to be a number
      const durationValue = Number(fileDuration);

      // ALWAYS set duration and isReady when we have a valid fileDuration
      setDuration(durationValue);
      setIsReady(true);

      // Store the fileDuration in a ref for easy access in other functions
      fileDurationRef.current = durationValue;

      // Also update the audio element's duration if possible
      if (audioRef.current) {
        try {
          // This is a read-only property in most browsers, but we'll try anyway
          audioRef.current._duration = durationValue;
          // Also set a custom property that we can access elsewhere
          audioRef.current.fileDuration = durationValue;
        } catch (e) {
          console.error('SimpleAudioPlayer: Could not update audio duration property:', e);
        }
      }
    }
  }, [fileDuration]); // Only run this effect when fileDuration changes, not src

  // Set up event listeners
  const handleCanPlay = () => {
    if (mountedRef.current && audioRef.current) {
      const audio = audioRef.current;
      setIsReady(true);

      // Only set duration if it's valid (not Infinity) and we don't have a fileDuration
      if (audio.duration > 0 && isFinite(audio.duration) && !fileDurationRef.current) {
        setDuration(audio.duration);
      }

      // Restore playback state if we were playing before a rerender
      if (wasPlayingRef.current && playbackPositionRef.current > 0) {
        // Set the current time to the saved position
        audio.currentTime = playbackPositionRef.current;

        // Resume playback
        audio.play().then(() => {
          // Playback resumed successfully
        }).catch(e => {
          // Reset the playing state since we failed to resume
          wasPlayingRef.current = false;
        });
      }
    }
  };

  // Handle time updates directly from the audio element
  const handleTimeUpdate = () => {
    if (mountedRef.current && audioRef.current) {
      const audio = audioRef.current;
      const newTime = audio.currentTime;

      // Get the current duration - prioritize fileDurationRef over audio.duration
      let currentDuration;
      if (fileDurationRef.current !== null && isFinite(fileDurationRef.current) && fileDurationRef.current > 0) {
        // Use fileDurationRef if available
        currentDuration = fileDurationRef.current;
      } else if (isFinite(audio.duration) && !isNaN(audio.duration) && audio.duration > 0) {
        // Use audio.duration if valid
        currentDuration = audio.duration;
      } else {
        // Fall back to state duration
        currentDuration = duration;
      }

      // ALWAYS update currentTime to ensure the progress bar moves
      setCurrentTime(newTime);

      // Save current position to ref for potential rerender recovery
      playbackPositionRef.current = newTime;

      // Only update duration from audio.duration if we don't have a valid fileDurationRef
      if (fileDurationRef.current === null && isFinite(audio.duration) && !isNaN(audio.duration) && audio.duration > 0 && Math.abs(audio.duration - duration) > 0.1) {
        setDuration(audio.duration);
      }

      // Check if we've reached the end of the audio (within 0.1 seconds)
      // This is a backup in case the 'ended' event doesn't fire properly
      if (currentDuration > 0 && newTime >= currentDuration - 0.1) {
        setIsPlaying(false);
        wasPlayingRef.current = false;
        clearProgressInterval();
      }
    }
  };

  // Add a loadedmetadata event listener to ensure we get the duration
  const handleLoadedMetadata = () => {
    if (mountedRef.current && audioRef.current) {
      const audio = audioRef.current;

      // Check if duration is valid and we don't have a fileDuration
      if (audio.duration > 0 && isFinite(audio.duration) && !fileDurationRef.current) {
        setDuration(audio.duration);
        setIsReady(true);
      } else if (!fileDurationRef.current) {
        // If duration is Infinity or invalid and we don't have a fileDuration, set a default duration
        setDuration(30);

        // Try to get duration again after a short delay
        setTimeout(() => {
          if (audioRef.current && audioRef.current.duration > 0 && isFinite(audioRef.current.duration)) {
            setDuration(audioRef.current.duration);
          }
        }, 1000);

        // Still set isReady to true so the player is usable
        setIsReady(true);
      }
    }
  };

  const handlePlay = () => {
    if (mountedRef.current && audioRef.current) {
      setIsPlaying(true);
      wasPlayingRef.current = true;
    }
  };

  const handlePause = () => {
    if (mountedRef.current && audioRef.current) {
      setIsPlaying(false);
      wasPlayingRef.current = false;
    }
  };

  // Reference to the manual update interval
  const progressIntervalRef = useRef(null);

  // Function to clear the progress interval
  const clearProgressInterval = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const handleEnded = () => {
    if (mountedRef.current && audioRef.current) {
      setIsPlaying(false);
      wasPlayingRef.current = false; // Also update the ref
      setCurrentTime(0);
      clearProgressInterval(); // Clear any active progress interval
    }
  };

  const handleError = (e) => {
    if (mountedRef.current && audioRef.current) {
      console.error('SimpleAudioPlayer: Audio error:', e);

      // Log more detailed error information
      if (e.target && e.target.error) {
        const errorCode = e.target.error.code;
        const errorTypes = {
          1: 'MEDIA_ERR_ABORTED - The user aborted the download.',
          2: 'MEDIA_ERR_NETWORK - A network error occurred while downloading.',
          3: 'MEDIA_ERR_DECODE - The browser failed to decode the file.',
          4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - The file format is not supported or the URL is invalid.'
        };

        console.error(`SimpleAudioPlayer: Error code: ${errorCode} - ${errorTypes[errorCode] || 'Unknown error'}`);

        // Check if this might be a CORS issue
        if (errorCode === 4) {
          console.error('SimpleAudioPlayer: This may be a CORS issue. Check network tab for details.');
          console.error('SimpleAudioPlayer: URL that failed:', e.target.src);
        }
      }

      setIsReady(false);
      setErrorMessage('Error loading audio. The file may not be available or the URL may have expired.');
    }
  };

  // Set up event listeners in a useEffect
  useEffect(() => {
    if (!audioRef.current) return;

    const audio = audioRef.current;

    // Add event listeners
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // Clean up on unmount - but preserve the audio element
    return () => {
      mountedRef.current = false;

      // Remove event listeners
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);

      // Pause but don't unload audio - this helps with persistence across rerenders
      if (audio.paused === false) {
        audio.pause();
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Handle src changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Reset state
    setIsReady(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0); // Reset duration to prevent accumulation
    setErrorMessage('');

    // If we have a valid fileDuration, set it immediately
    if (fileDuration !== null && isFinite(fileDuration) && fileDuration > 0) {
      const durationValue = Number(fileDuration);
      setDuration(durationValue);
      setIsReady(true);
      fileDurationRef.current = durationValue;
    } else {
      fileDurationRef.current = null;
    }

    if (!src) {
      // If no src provided, try the local test file directly
      // Set audio properties before loading the source
      audio.preload = 'auto'; // Ensure the audio is fully preloaded
      audio.loop = false; // Don't loop the audio

      // Force the browser to preload the entire audio file
      audio.load();

      // Use an absolute URL to ensure the file is found
      const absoluteUrl = new URL('/test.wav', window.location.origin).href;

      audio.src = absoluteUrl;
      setUsingLocalFile(true);

      // Add a loadeddata event listener to log when the file is fully loaded
      const handleLoaded = () => {
        audio.removeEventListener('loadeddata', handleLoaded);

        // Set isReady to true when the file is loaded
        if (audio.duration > 0 && isFinite(audio.duration) && !fileDurationRef.current) {
          setDuration(audio.duration);
          setIsReady(true);
        }
      };
      audio.addEventListener('loadeddata', handleLoaded, { once: true });

      audio.load();
      return;
    }

    // Reset usingLocalFile flag when trying a new source
    setUsingLocalFile(false);

    // Try with the provided src first
    audio.src = src;

    // Add a loadeddata event listener to set isReady when the file is loaded
    const handleLoaded = () => {
      // Set isReady to true when the file is loaded
      if (audio.duration > 0 && isFinite(audio.duration) && !fileDurationRef.current) {
        setDuration(audio.duration);
        setIsReady(true);
      } else if (!isReady) {
        // If we're not already ready (from fileDuration), set ready now
        setIsReady(true);
      }
    };
    audio.addEventListener('loadeddata', handleLoaded, { once: true });

    // Add an error handler that will try a local file if the remote one fails
    const handleInitialError = () => {
      audio.removeEventListener('error', handleInitialError);

      // Update error message to indicate we're trying a fallback
      if (mountedRef.current) {
        setErrorMessage('Remote audio failed to load. Trying local test file...');
        setUsingLocalFile(true);
      }

      // Set audio properties before loading the source
      audio.preload = 'auto'; // Ensure the audio is fully preloaded
      audio.loop = false; // Don't loop the audio

      // Use an absolute URL to ensure the file is found
      const absoluteUrl = new URL('/test.wav', window.location.origin).href;
      console.error('SimpleAudioPlayer: Loading local file from absolute URL:', absoluteUrl);

      audio.src = absoluteUrl;
      audio.load();

      // Add a loadeddata event listener to log when the file is fully loaded
      const handleLoaded = () => {
        audio.removeEventListener('loadeddata', handleLoaded);

        // Set isReady to true when the file is loaded
        if (audio.duration > 0 && isFinite(audio.duration)) {
          setDuration(audio.duration);
          setIsReady(true);
        }
      };
      audio.addEventListener('loadeddata', handleLoaded, { once: true });
    };

    audio.addEventListener('error', handleInitialError, { once: true });
    audio.load();

    // Autoplay if specified
    if (autoPlay) {
      audio.play().catch(e => {
        console.error('SimpleAudioPlayer: Autoplay failed:', e);
      });
    }

    // Clean up the special error handler when the effect is cleaned up
    return () => {
      audio.removeEventListener('error', handleInitialError);
      audio.removeEventListener('loadeddata', handleLoaded);
    };
  }, [src, autoPlay, fileDuration, isReady]);

  // Toggle play/pause
  const togglePlayPause = (e) => {
    // Prevent default browser behavior and stop event propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const audio = audioRef.current;
    if (!audio) return;


    if (isPlaying) {
      try {
        audio.pause();
        // Manually update state to ensure UI reflects the change
        setIsPlaying(false);
        wasPlayingRef.current = false;
      } catch (e) {
        console.error('SimpleAudioPlayer: Error pausing audio:', e);
      }
    } else {
      // Get the effective duration - prioritize fileDurationRef over audio.duration
      let effectiveDuration;
      if (fileDurationRef.current !== null && isFinite(fileDurationRef.current) && fileDurationRef.current > 0) {
        effectiveDuration = fileDurationRef.current;
      } else if (isFinite(audio.duration) && !isNaN(audio.duration) && audio.duration > 0) {
        effectiveDuration = audio.duration;
      } else {
        effectiveDuration = duration; // Fall back to state duration
      }

      // Ensure we're at the beginning if we've reached the end
      if (audio.currentTime >= effectiveDuration) {
        audio.currentTime = 0;
      }

      // Set audio properties to ensure it plays completely
      audio.preload = 'auto';
      audio.loop = false; // Don't loop

      // Ensure volume is set properly
      audio.volume = 1.0;

      // Play with detailed error handling
      try {
        // Manually update state to ensure UI reflects the change immediately
        setIsPlaying(true);
        wasPlayingRef.current = true;

        const playPromise = audio.play();

        // Handle the play promise
        if (playPromise !== undefined) {
          playPromise.then(() => {
            // Clear any existing interval first
            clearProgressInterval();

            // Set up a timer to log playback progress and store the reference
            progressIntervalRef.current = setInterval(() => {
              if (audio && !audio.paused) {
                // Force a state update to ensure the progress bar updates
                setCurrentTime(audio.currentTime);
              } else {
                clearProgressInterval();
              }
            }, 250); // Check more frequently (250ms) to ensure smoother updates

            // Clear the interval after the expected duration plus buffer
            setTimeout(() => {
              clearProgressInterval();
            }, (effectiveDuration + 5) * 1000); // Duration plus 5 seconds buffer

          }).catch(e => {
            // Only handle the error if it's not an AbortError (which is expected when pausing)
            if (e.name !== 'AbortError') {
              setErrorMessage('Failed to play audio. Please try again.');

              // Reset playing state since play failed
              setIsPlaying(false);
              wasPlayingRef.current = false;

              // Try to diagnose the issue
              // console.error('SimpleAudioPlayer: Audio state at time of error:');
              // console.error('- src:', audio.src);
              // console.error('- currentTime:', audio.currentTime);
              // console.error('- duration:', audio.duration);
              // console.error('- fileDurationRef:', fileDurationRef.current);
              // console.error('- paused:', audio.paused);
              // console.error('- ended:', audio.ended);
              // console.error('- error:', audio.error);
            }
          });
        }
      } catch (e) {
        console.error('SimpleAudioPlayer: Exception during play():', e);
        // Reset playing state since play failed
        setIsPlaying(false);
        wasPlayingRef.current = false;
        setErrorMessage('Failed to play audio. Please try again.');
      }
    }
  };

  // Handle seeking
  const handleSeek = (e, { value }) => {
    const audio = audioRef.current;
    if (!audio || !isReady) return;

    // Get the effective duration
    const effectiveDuration =
      (fileDurationRef.current !== null && isFinite(fileDurationRef.current) && fileDurationRef.current > 0)
        ? fileDurationRef.current
        : (isFinite(duration) && !isNaN(duration) && duration > 0 ? duration : 100);

    // Ensure value is within bounds
    const newTime = Math.min(Math.max(0, value), effectiveDuration);
    // Set the audio's current time
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Handle reload
  const handleReload = () => {

    // Call the onReload callback if provided
    if (onReload) {
      onReload();
      return; // Let the parent handle the reload
    }

    // Otherwise, reload the audio ourselves
    const audio = audioRef.current;
    if (!audio || !src) return;

    // Reset state
    setIsReady(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setErrorMessage('');

    // Reload the audio
    audio.pause();
    audio.currentTime = 0;
    audio.src = src;
    audio.load();
  };

  return (
    <div
      className="simple-audio-player-wrapper"
      onClick={(e) => e.stopPropagation()} // Prevent clicks from bubbling up
    >
      <div className="simple-audio-player">
        {header && (
          <div className="player-header">
            {header}
            {usingLocalFile && isReady && (
              <div className="test-file-indicator">
                Using Test File
              </div>
            )}
          </div>
        )}

        <Grid>
          <Grid.Row columns={3} verticalAlign="middle">
            <Grid.Column width={1}>
              <Button
                icon
                circular
                size="mini"
                onClick={togglePlayPause}
                disabled={!isReady}
                className="audio-player-button"
                type="button" // Explicitly set type to button to prevent form submission
              >
                <Icon name={isPlaying ? 'pause' : 'play'} style={{ verticalAlign: 'none !important' }} />
              </Button>

              {/* Show a small indicator when using local test file */}
              {usingLocalFile && isReady && (
                <div className="test-file-badge">
                  T
                </div>
              )}
            </Grid.Column>

            <Grid.Column width={13}>
              {errorMessage ? (
                <div className="audio-error-message">
                  <div className="error-title">
                    <Icon name="warning sign" />
                    <span>{errorMessage}</span>
                  </div>
                  <div className="error-details">
                    Try clicking the reload button or refreshing the page. If the issue persists, the file may still be processing.
                  </div>
                </div>
              ) : (
                <div className="progress-container">
                  {/* Raw time values for debugging - only showing current time */}
                  <div className="debug-time-display" style={{ textAlign: 'center', fontSize: '0.8em', color: '#666' }}>
                    {currentTime.toFixed(2)}s
                  </div>

                  {/* Use Semantic UI Progress component */}
                  <Progress
                    value={currentTime}
                    total={
                      (fileDurationRef.current !== null && isFinite(fileDurationRef.current) && fileDurationRef.current > 0)
                        ? fileDurationRef.current
                        : (isFinite(duration) && !isNaN(duration) && duration > 0 ? duration : 100)
                    }
                    size="tiny"
                    style={{ marginBottom: '5px', marginTop: '5px' }}
                    disabled={!isReady}
                    onClick={handleSeek}
                  />

                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em' }}>
                    <span>{formatTime(duration || 0)}</span>
                  </div>
                </div>
              )}
            </Grid.Column>

            <Grid.Column width={2} textAlign="right">
              <Button
                icon="refresh"
                size="mini"
                title="Reload Audio"
                onClick={(e) => {
                  // Prevent default browser behavior and stop event propagation
                  if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  handleReload();
                }}
                type="button" // Explicitly set type to button to prevent form submission
                className="audio-player-button"
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </div>
  );
};

export default SimpleAudioPlayer;
